import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import {
  accountEmailsQuery,
  adminAthletesQuery,
  adminEnrollmentsQuery,
  clubNetCents,
  contactMessagesQuery,
  downloadCsv,
  preregistrationsQuery,
  stripeStatusQuery,
  type AdminEnrollment,
} from "@/lib/admin";
import { formatPrice } from "@/lib/family";
import { gradeLabel } from "@/lib/grades";

export const Route = createFileRoute("/_app/admin")({
  beforeLoad: ({ context }) => {
    if (context.profile.role !== "admin") throw redirect({ to: "/home" });
  },
  component: AdminHome,
});

const TABS = ["Registrations", "Players", "Pre-registrations", "Messages"] as const;
type Tab = (typeof TABS)[number];

function fmtDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
}

function fullName(p: { first_name: string | null; last_name: string | null } | null) {
  return [p?.first_name, p?.last_name].filter(Boolean).join(" ");
}

function playerName(a: AdminEnrollment["athlete"]) {
  return a ? `${a.preferred_name || a.first_name} ${a.last_name}` : "";
}

function statusLabel(e: AdminEnrollment) {
  if (e.status === "paid") return "Paid";
  if (e.status === "refunded") return "Refunded";
  if (e.status === "cancelled") return "Cancelled";
  return e.payment_submitted_at ? "Bank processing" : "Started, not paid";
}

// Staff dashboard: registrations and payments, players, website pre-registrations and messages.
function AdminHome() {
  const { profile } = Route.useRouteContext();
  const [tab, setTab] = useState<Tab>("Registrations");
  const enrollments = useQuery(adminEnrollmentsQuery);
  const stripe = useQuery(stripeStatusQuery);

  const paid = (enrollments.data ?? []).filter((e) => e.status === "paid");
  const collected = paid.reduce((s, e) => s + (e.amount_paid_cents ?? e.price_cents), 0);
  const net = paid.reduce((s, e) => s + clubNetCents(e), 0);
  const processing = (enrollments.data ?? []).filter((e) => e.status === "pending" && e.payment_submitted_at).length;

  return (
    <div className="space-y-8">
      <section>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-a1-red">Admin</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold uppercase italic">
          Hi{profile.first_name ? `, ${profile.first_name}` : ""}.
        </h1>
        <p className="mt-2 text-sm text-white/60">
          {stripe.data?.stripe_account_id
            ? `Payments connected to Stripe since ${fmtDate(stripe.data.stripe_connected_at)}.`
            : "Payments are not connected to Stripe yet."}
        </p>
      </section>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Players registered" value={String(paid.length)} />
        <Kpi label="Collected" value={formatPrice(collected)} />
        <Kpi label="Club net (est.)" value={formatPrice(net)} />
        <Kpi label="Bank payments processing" value={String(processing)} />
      </section>

      <nav className="flex flex-wrap gap-2 border-b border-white/10 pb-3">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold ${tab === t ? "bg-white text-a1-black" : "text-white/70 hover:text-white"}`}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === "Registrations" && <RegistrationsTab rows={enrollments.data} loading={enrollments.isLoading} />}
      {tab === "Players" && <PlayersTab />}
      {tab === "Pre-registrations" && <PreregistrationsTab />}
      {tab === "Messages" && <MessagesTab />}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.03] p-4">
      <p className="text-xs text-white/60">{label}</p>
      <p className="mt-1 font-display text-2xl font-extrabold italic">{value}</p>
    </div>
  );
}

function Table({ head, children, empty }: { head: string[]; children: ReactNode; empty: boolean }) {
  return (
    <div className="overflow-x-auto rounded-md border border-white/10">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead className="bg-white/5 text-xs uppercase tracking-wide text-white/60">
          <tr>
            {head.map((h) => (
              <th key={h} className="px-3 py-2 font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {empty ? (
            <tr>
              <td colSpan={head.length} className="px-3 py-6 text-center text-white/50">
                Nothing here yet.
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}

function TabHeader({ title, onExport }: { title: string; onExport?: () => void }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="font-display text-2xl font-extrabold uppercase italic">{title}</h2>
      {onExport && (
        <button type="button" onClick={onExport} className="rounded-md border border-white/20 px-3 py-1.5 text-sm hover:border-white/40">
          Download CSV
        </button>
      )}
    </div>
  );
}

function RegistrationsTab({ rows, loading }: { rows?: AdminEnrollment[]; loading: boolean }) {
  const data = rows ?? [];
  function exportCsv() {
    downloadCsv(
      "a1-registrations.csv",
      ["Date", "Player", "Grade", "Program", "Parent", "Email", "Phone", "Status", "Method", "Paid", "Club net (est.)"],
      data.map((e) => [
        fmtDate(e.paid_at ?? e.created_at),
        playerName(e.athlete),
        e.athlete ? gradeLabel(e.athlete.grade) : "",
        e.program?.name,
        fullName(e.parent),
        e.parent?.email,
        e.parent?.phone,
        statusLabel(e),
        e.payment_method === "ach" ? "Bank" : e.payment_method === "card" ? "Card" : "",
        e.status === "paid" ? ((e.amount_paid_cents ?? e.price_cents) / 100).toFixed(2) : "",
        e.status === "paid" ? (clubNetCents(e) / 100).toFixed(2) : "",
      ]),
    );
  }
  return (
    <section>
      <TabHeader title="Registrations" onExport={data.length ? exportCsv : undefined} />
      {loading ? (
        <p className="text-sm text-white/60">Loading…</p>
      ) : (
        <Table head={["Date", "Player", "Program", "Parent", "Status", "Paid"]} empty={!data.length}>
          {data.map((e) => (
            <tr key={e.id}>
              <td className="px-3 py-2 text-white/70">{fmtDate(e.paid_at ?? e.created_at)}</td>
              <td className="px-3 py-2">
                {playerName(e.athlete)}
                <span className="block text-xs text-white/50">{e.athlete ? gradeLabel(e.athlete.grade) : ""}</span>
              </td>
              <td className="px-3 py-2">{e.program?.name}</td>
              <td className="px-3 py-2">
                {fullName(e.parent)}
                <span className="block text-xs text-white/50">{e.parent?.email}</span>
              </td>
              <td className={`px-3 py-2 ${e.status === "paid" ? "text-emerald-400" : "text-white/60"}`}>{statusLabel(e)}</td>
              <td className="px-3 py-2">
                {e.status === "paid" ? formatPrice(e.amount_paid_cents ?? e.price_cents) : ""}
                {e.payment_method && <span className="block text-xs text-white/50">{e.payment_method === "ach" ? "Bank" : "Card"}</span>}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </section>
  );
}

function PlayersTab() {
  const { data = [], isLoading } = useQuery(adminAthletesQuery);
  function exportCsv() {
    downloadCsv(
      "a1-players.csv",
      ["Player", "Grade", "Date of birth", "Parent", "Email", "Phone", "Emergency contact", "Emergency phone"],
      data.map((a) => {
        const g = a.guardians[0]?.profile ?? null;
        return [
          `${a.preferred_name || a.first_name} ${a.last_name}`,
          gradeLabel(a.grade),
          a.date_of_birth,
          fullName(g),
          g?.email,
          g?.phone,
          a.emergency_contact_name,
          a.emergency_contact_phone,
        ];
      }),
    );
  }
  return (
    <section>
      <TabHeader title={`Players (${data.length})`} onExport={data.length ? exportCsv : undefined} />
      {isLoading ? (
        <p className="text-sm text-white/60">Loading…</p>
      ) : (
        <Table head={["Player", "Grade", "Parent", "Phone", "Emergency contact"]} empty={!data.length}>
          {data.map((a) => {
            const g = a.guardians[0]?.profile ?? null;
            return (
              <tr key={a.id}>
                <td className="px-3 py-2">
                  {a.preferred_name || a.first_name} {a.last_name}
                </td>
                <td className="px-3 py-2">{gradeLabel(a.grade)}</td>
                <td className="px-3 py-2">
                  {fullName(g)}
                  <span className="block text-xs text-white/50">{g?.email}</span>
                </td>
                <td className="px-3 py-2">{g?.phone}</td>
                <td className="px-3 py-2">
                  {a.emergency_contact_name}
                  <span className="block text-xs text-white/50">{a.emergency_contact_phone}</span>
                </td>
              </tr>
            );
          })}
        </Table>
      )}
    </section>
  );
}

function PreregistrationsTab() {
  const { data = [], isLoading } = useQuery(preregistrationsQuery);
  const accounts = useQuery(accountEmailsQuery);
  const hasAccount = (email: string) => accounts.data?.has(email.toLowerCase()) ?? false;
  function exportCsv() {
    downloadCsv(
      "a1-preregistrations.csv",
      ["Date", "Player", "Grade", "Parent", "Email", "Phone", "Source", "Has A1 Connect account"],
      data.map((p) => [
        fmtDate(p.created_at),
        p.player_name,
        p.grade,
        p.parent_name,
        p.email,
        p.phone,
        p.source === "website" ? "Website" : "Triumph",
        hasAccount(p.email) ? "Yes" : "No",
      ]),
    );
  }
  return (
    <section>
      <TabHeader title={`Pre-registrations (${data.length})`} onExport={data.length ? exportCsv : undefined} />
      {isLoading ? (
        <p className="text-sm text-white/60">Loading…</p>
      ) : (
        <Table head={["Date", "Player", "Grade", "Parent", "Source", "Account"]} empty={!data.length}>
          {data.map((p) => (
            <tr key={p.id}>
              <td className="px-3 py-2 text-white/70">{fmtDate(p.created_at)}</td>
              <td className="px-3 py-2">{p.player_name}</td>
              <td className="px-3 py-2">{p.grade}</td>
              <td className="px-3 py-2">
                {p.parent_name}
                <span className="block text-xs text-white/50">
                  {p.email}
                  {p.phone ? ` · ${p.phone}` : ""}
                </span>
              </td>
              <td className="px-3 py-2 text-white/70">{p.source === "website" ? "Website" : "Triumph"}</td>
              <td className={`px-3 py-2 ${hasAccount(p.email) ? "text-emerald-400" : "text-white/50"}`}>
                {hasAccount(p.email) ? "Signed up" : "Not yet"}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </section>
  );
}

function MessagesTab() {
  const { data = [], isLoading } = useQuery(contactMessagesQuery);
  return (
    <section>
      <TabHeader title={`Messages (${data.length})`} />
      {isLoading ? (
        <p className="text-sm text-white/60">Loading…</p>
      ) : data.length === 0 ? (
        <p className="text-sm text-white/50">No messages yet.</p>
      ) : (
        <ul className="space-y-3">
          {data.map((m) => (
            <li key={m.id} className="rounded-md border border-white/10 bg-white/[0.03] p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-semibold">
                  {m.name}{" "}
                  <a href={`mailto:${m.email}`} className="text-sm font-normal text-white/60 underline underline-offset-2">
                    {m.email}
                  </a>
                </p>
                <span className="text-xs text-white/50">{fmtDate(m.created_at)}</span>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-white/80">{m.message}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
